"use client";
import axios, { AxiosError } from "axios";
import { useRouter } from "next/router";
import {
    FormEventHandler,
    ReactElement,
    SyntheticEvent,
    useEffect,
    useState,
} from "react";

import { handleGoodlifeFormSubmit } from "@/api/competitions/createGoodlifeLead";
import { CallToActionButton, LeadFormButtonVariant } from "@/components/landing/CallToActionButton";
import { FormFields } from "@/components/landing/FormFields";
import { COMPLETED_COMPETITION_DRAW_ID_COOKIE } from "@/constants";
import { FormFieldAttributes } from "@/types/landing";
import { CompetitionPageTemplate } from "@/types/competitions";
import { getClientCookie, setCookie } from "@/utils/cookies/client";

import type { Site } from "@/types/sites";
import type { StrapiEntity } from "@/types/strapi";

import styles from "./CompetitionPage.module.css";

interface CompetitionFormProps {
    site: Site;
    competitionPage: CompetitionPageTemplate;
    redirectUrl?: string;
}

type FieldErrorType = "full_name" | "email" | "phone" | "none";
interface ValidationErrors {
    first_name?: string[];
    last_name?: string[];
    email?: string[];
    phone?: string[];
    opted_in_email?: string[];
    opted_in_phone?: string[];
    draw_id?: string[];
    list_id?: string[];
    [key: string]: string[] | undefined;
}

export const CompetitionForm = ({
    site,
    competitionPage,
    redirectUrl,
}: CompetitionFormProps): ReactElement => {
    const router = useRouter();
    useEffect(() => {
        if (redirectUrl) {
            try {
                const prefetchUrl = new URL(`${window.location.origin}/${redirectUrl}`);
                prefetchUrl.searchParams.set("prefetch", "true");
                router.prefetch(prefetchUrl.toString());
            } catch (error) {
                console.error("Error prefetching", error);
            }
        }
    }, [redirectUrl, router]);

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formError, setFormError] = useState<string | null>(null);
    const fields: StrapiEntity<Partial<FormFieldAttributes>>[] = [
        {
            id: 15,
            attributes: {
                name: "Full Name (Required)",
                placeholder: "Full name",
                fieldText: "",
                fieldType: "text",
                autoComplete: "name",
                isRequired: true,
                bqMapping: ["full_name"],
            },
        },
        {
            id: 1,
            attributes: {
                name: "Email (Required)",
                placeholder: "Email",
                fieldText: "",
                fieldType: "email",
                autoComplete: "email",
                isRequired: true,
                bqMapping: ["email"],
            },
        },
        {
            id: 10,
            attributes: {
                name: "Phone Number (Required)",
                placeholder: "Phone",
                fieldType: "tel",
                autoComplete: "off",
                isRequired: true,
                bqMapping: ["phone_number"],
            },
        },
        {
            id: 16,
            attributes: {
                name: "Goodlife Opt-In",
                placeholder: undefined,
                fieldText:
                    'By entering your details, you confirm that you are 18 or over and consent to be contacted if you win. You also agree to receive marketing communications via email, SMS, and phone from us and Good Life+. Your information will be used in accordance with our <a href="/policy/privacy?region=wow-gl">Privacy Policy</a>, and you can unsubscribe at any time. By continuing, you accept our <a href="/policy/terms-and-conditions?region=wow-gl">Terms & Conditions</a>.',
                fieldType: "auto opt-in",
                autoComplete: "off",
                isRequired: true,
                bqMapping: ["subscribe_email", "subscribe_phone"],
            },
        },
    ];

    const handleValidationErrors = (errors: ValidationErrors) => {
        setFieldErrors("none");
        setFormError(null);
        if (errors?.first_name || errors?.last_name) {
            setFieldErrors("full_name");
            setFormError("Please enter your full name");
        } else if (errors.email) {
            setFieldErrors("email");
            setFormError("Please enter a valid email address");
        } else if (errors.phone) {
            setFieldErrors("phone");
            setFormError("Please enter a valid phone number");
        } else if (errors.opted_in_email || errors.opted_in_phone) {
            setFormError("You must opt in to marketing to enter");
        } else {
            setFormError("Unknown error, please try again later");
        }
    };

    const handleSuccess = (
        formData: FormData,
        leadId: string | null,
        errorMessage?: string
    ) => {
        console.log("handleSuccess", formData, leadId, errorMessage);
        const formDataObject = Object.fromEntries(formData.entries());

        if (competitionPage.draw_id && competitionPage.list_id) {
            const competitionId = `${competitionPage.draw_id}_${competitionPage.list_id}`;
            const existingCompletedCompetitions = getClientCookie(COMPLETED_COMPETITION_DRAW_ID_COOKIE);
            let completedCompetitions = [];
            
            try {
                if (existingCompletedCompetitions) {
                    completedCompetitions = JSON.parse(existingCompletedCompetitions);
                    if (!Array.isArray(completedCompetitions)) {
                        completedCompetitions = [];
                    }
                }
                if (!completedCompetitions.includes(competitionId)) {
                    completedCompetitions.push(competitionId);
                }
                setCookie(
                    COMPLETED_COMPETITION_DRAW_ID_COOKIE, 
                    JSON.stringify(completedCompetitions), 
                    (30 * 24 * 60 * 60* 1000)
                );
            } catch (e) {
                setCookie(
                    COMPLETED_COMPETITION_DRAW_ID_COOKIE, 
                    JSON.stringify([competitionId]), 
                    (30 * 24 * 60 * 60* 1000)
                );
            }
        }
        
        Promise.all([
            router.push({
                pathname: redirectUrl || "/",
                query: router.query,
            }),
            handleGoodlifeFormSubmit(
                site,
                competitionPage,
                formDataObject,
                leadId,
                errorMessage
            ),
        ]);
    };

    const setFieldErrors = (field: FieldErrorType) => {
        document
            .querySelectorAll(`.${styles["goodlife-error-input"]}`)
            .forEach((el) =>
                el.classList.remove(styles["goodlife-error-input"])
            );

        if (field === "none") return;
        const input = document.querySelector<HTMLInputElement>(
            `input[name="${field}"]`
        );
        if (input) {
            input.focus();
            input.classList.add(styles["goodlife-error-input"]);
            input.addEventListener("change", (event) => {
                const target = event.target as HTMLInputElement;
                target.classList.remove(styles["goodlife-error-input"]);
                setFormError(null);
            });
        }
        setIsSubmitting(false);
    };

    const handleSubmit: FormEventHandler<HTMLFormElement> = async (
        e: SyntheticEvent<HTMLFormElement, SubmitEvent>
    ): Promise<void> => {
        e.preventDefault();
        setIsSubmitting(true);
        setFormError(null);
        setFieldErrors("none");
        const form = e.target as HTMLFormElement;
        const formData = new FormData(form);
        const formDataObject = Object.fromEntries(formData.entries());
        try {
            const response = await axios.post("/api/form/goodlifeForm", {
                formData: formDataObject,
                competitionData: {
                    draw_id: competitionPage.draw_id,
                    list_id: competitionPage.list_id,
                },
            });
            if (response.data.data.lead_id) {
                handleSuccess(formData, response.data.data.lead_id);
            } else {
                console.log("no lead id", response.data);
                setFormError("Server error, please try again later");
                new Error("No lead id");
            }
        } catch (error) {
            const axiosError = error as AxiosError<any>;
            console.log("axiosError", axiosError);
            if (axiosError.response) {
                const { status, data } = axiosError.response;

                switch (status) {
                    case 400:
                        // Bad request - missing parameters
                        setFormError(
                            data.message || "Missing required information"
                        );
                        break;
                    case 401:
                        // unauthenticarted - should store in bigquery and pretend its successful
                        await handleSuccess(formData, null, data.message);
                        break;
                    case 422: {
                        // Validation errors - store in bq if only draw_id or list_id are missing
                        const errors = data.errors || {};
                        const errorKeys = Object.keys(errors);
                        if (
                            errorKeys.length > 0 &&
                            errorKeys.every(
                                (key) => key === "draw_id" || key === "list_id"
                            )
                        ) {
                            await handleSuccess(formData, null, data.message);
                        } else if (
                            data.message == "Invalid List ID" ||
                            data.message == "Invalid Draw ID"
                        ) {
                            await handleSuccess(formData, null, data.message);
                        } else {
                            handleValidationErrors(data.errors);
                        }
                        break;
                    }
                    case 500:
                    case 502:
                        // Server error - should store in bigquery and pretend its successful
                        await handleSuccess(formData, null, data.message);
                        break;
                    default:
                        // Other errors - show generic error
                        setFormError(
                            data.message ||
                                "An unexpected error occurred, please try again later"
                        );
                }
            } else {
                // Network error or other issues
                setFormError(
                    "Connection error. Please check your internet and try again."
                );
            }
        }
    };

    return (
        <div className={styles["goodlife-form"]}>
            <span className={styles["goodlife-badge"]}>
                {competitionPage.tagline}
            </span>
            <h2
                className={styles["goodlife-title"]}
                dangerouslySetInnerHTML={{
                    __html: sanitizeTitle(competitionPage.title),
                }}
            />
            {isSubmitting && (
                <div className={styles["goodlife-loading"]}>
                    <span className={styles["loader"]}></span>
                </div>
            )}
            <form
                id="competition-form"
                onSubmit={handleSubmit}
                data-list-id={competitionPage.list_id}
                data-draw-id={competitionPage.draw_id}
                className={isSubmitting ? styles["form-loading"] : ""}
            >
                {formError && (
                    <div className={styles["goodlife-error"]}>{formError}</div>
                )}
                {fields && fields.length > 0 && (
                    <FormFields
                        formFields={{
                            data: fields as StrapiEntity<FormFieldAttributes>[],
                        }}
                        buttonText={""}
                        providers={[]}
                        site={site}
                        landingPage={null}
                        isLoading={isSubmitting}
                    />
                )}
                <CallToActionButton
                    buttonText={competitionPage.cta_text || "Enter for free"}
                    variant={LeadFormButtonVariant.fullWidthWithoutFields}
                    disabled={isSubmitting}
                />
            </form>
        </div>
    );
};

const sanitizeTitle = (htmlString: string): string => {
    if (!htmlString.includes("<") && !htmlString.includes("-")) {
        return htmlString;
    }
    const sanitized = htmlString.replace(/-/g, "‑");
    return sanitized.replace(/<(?!span\b)[^>]*>/g, "");
};
